import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, Plus, Building2, Trash2, MapPin, Users, Gem, Shield } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState } from "@/components/common/EmptyState";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { PROPERTY_TYPES } from "@/lib/risk";

const SECURITY_OPTIONS = ["Noise monitor", "Security camera (exterior)", "Smart lock", "Alarm system", "Gated access", "On-site manager"];

const DEFAULTS = { name: "", suburb: "", property_type: "Apartment", max_guests: 4, luxury_level: "Standard", party_sensitivity: "Medium", security_features: [] };

const CARD_ANIM = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 } };

export default function Properties() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULTS);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/properties");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSecurity = (opt) => setForm((f) => ({
    ...f,
    security_features: f.security_features.includes(opt) ? f.security_features.filter((x) => x !== opt) : [...f.security_features, opt],
  }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Property name is required"); return; }
    setSaving(true);
    try {
      await api.post("/properties", { ...form, max_guests: Number(form.max_guests) || 1 });
      toast.success("Property added");
      setOpen(false); setForm(DEFAULTS); setLoading(true); load();
    } catch {
      toast.error("Could not save property");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/properties/${id}`); toast.success("Property removed"); setItems((x) => x.filter((p) => p.id !== id)); }
    catch { toast.error("Could not remove"); }
  };

  const AddDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="gap-2" data-testid="add-property-button"><Plus className="h-4 w-4" /> Add property</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Add a property</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5"><Label>Property name</Label><Input value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="e.g. Bondi Beach Apartment" data-testid="property-name-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Suburb</Label><Input value={form.suburb} onChange={(e) => set("suburb")(e.target.value)} placeholder="e.g. Bondi" data-testid="property-suburb-input" /></div>
            <div className="space-y-1.5"><Label>Max guests</Label><Input type="number" min={1} value={form.max_guests} onChange={(e) => set("max_guests")(e.target.value)} data-testid="property-max-guests-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Property type</Label>
              <Select value={form.property_type} onValueChange={set("property_type")}>
                <SelectTrigger data-testid="property-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PROPERTY_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Luxury level</Label>
              <Select value={form.luxury_level} onValueChange={set("luxury_level")}>
                <SelectTrigger data-testid="property-luxury-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Standard", "Premium", "Luxury"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Party sensitivity</Label>
            <Select value={form.party_sensitivity} onValueChange={set("party_sensitivity")}>
              <SelectTrigger data-testid="property-party-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["Low", "Medium", "High"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Security features</Label>
            <div className="flex flex-wrap gap-2">
              {SECURITY_OPTIONS.map((o) => (
                <button type="button" key={o} onClick={() => toggleSecurity(o)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${form.security_features.includes(o) ? "border-primary bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-accent"}`}>
                  {o}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="property-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save property"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <AppShell title="Properties" action={AddDialog}>
      {loading ? (
        <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <EmptyState icon={Building2} title="No properties yet" description="Add your properties to link them to assessments and tailor risk to each home." action={AddDialog} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <motion.div key={p.id} {...CARD_ANIM} className="rounded-xl border bg-card p-5" data-testid={`property-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent"><Building2 className="h-5 w-5 text-primary" /></div>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--risk-high))]" onClick={() => remove(p.id)} data-testid={`property-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <h3 className="mt-3 font-display text-base font-semibold">{p.name}</h3>
              <p className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {p.suburb || "—"} · {p.property_type}</p>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <span className="flex items-center gap-1.5 text-muted-foreground"><Users className="h-3.5 w-3.5" /> Max {p.max_guests}</span>
                <span className="flex items-center gap-1.5 text-muted-foreground"><Gem className="h-3.5 w-3.5" /> {p.luxury_level}</span>
                <span className="flex items-center gap-1.5 text-muted-foreground"><Shield className="h-3.5 w-3.5" /> Party: {p.party_sensitivity}</span>
              </div>
              {p.security_features?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {p.security_features.map((s) => <span key={s} className="rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground">{s}</span>)}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
